import {Button} from "primereact/button";
import {Chart} from "primereact/chart";
import {Checkbox} from "primereact/checkbox";
import {Column} from "primereact/column";
import {ColumnGroup} from "primereact/columngroup";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {Row} from "primereact/row";
import {SelectButton} from "primereact/selectbutton";
import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {MimeType} from "@reva/shared/mime-type";

import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import MissingPermissionIndicator from "../../component/MissingPermissionIndicator";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useCheckPermission} from "../../hooks/permissionHooks";
import {selectAddressData, selectAddressSelected} from "../../redux/addressSlice";
import {selectTimelineDataAggregated} from "../../redux/locationInfoSelectors";
import {selectDescriptionData, selectTimelinesError, selectTimelinesLoading} from "../../redux/locationInfoSlice";
import {selectDarkTheme} from "../../redux/themeSlice";

import "chartjs-adapter-date-fns";

import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";

import {SEGMENT} from "@reva/shared/reva-spec";
import {TIMELINE_REGIONAL_LEVEL_VALUES, TimelineSegmentType} from "@reva/shared/timelines";

import {useParameterValue} from "../../component/paramInput/paramHooks";
import {selectSegment} from "../../redux/objectParametersSlice";
import {fetchTimelinesDocumentation} from "../../redux/pdfThunk";
import {selectPersistenceLoading} from "../../redux/persistenceSlice";
import {useRevaSelector} from "../../redux/revaHooks";
import {openBlobForDownload} from "../../util/browserUtil";
import {getChartStyle} from "../../util/chartUtils";
import {PERMISSION_RULE_MARKET_TIMELINES} from "../../util/permissions";
import {getObjectTypeBySegment, getObjectTypeLabelI18n, REGIONAL_LEVEL_DATA} from "../../util/timelineAggregation";
import {getTimelineChartData, getTimelineChartOptions} from "../../util/timelineChart";
import {getTimelineTableData} from "../../util/timelineTable";
import {getObjectTypes, getSupportedRegionalLevelsForObjectType, getTimelineSegments} from "../../util/timelineUtil";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";

const DOC_PROPERTY_MARKET_DESCRIPTION_MARKET = "market_description_market";

/** @typedef {import("primereact/selectbutton").SelectButtonChangeEvent} SelectButtonChangeEvent */

/**
 * @param {TimelineTableData|null} timelineTableData
 * @param {TimelineObjectType} objectType
 * @param {TimelineRegionalLevel} regionalLevel
 */
function TimelinesTable({timelineTableData, objectType, regionalLevel}) {
    /** @type {PersistenceAddressData} */
    const addressData = useSelector(selectAddressData);
    const toastRef = useRef(null);

    const {t} = useTranslation();

    const {parameterValues} = useParameterValue(DOC_PROPERTY_MARKET_DESCRIPTION_MARKET, "DOC_PROPERTY");
    const marketTextDefault = useSelector((state) => selectDescriptionData(state)?.market_text ?? null);
    // activeParameterValue ist untypisiert (any); String() hält den Split im Kopier-Pfad robust.
    const marketDescription = String(parameterValues.activeParameterValue ?? marketTextDefault ?? "");

    if (!timelineTableData || !addressData) return null;

    const regionalInfo = regionalLevel ? (
        <span>
            {" "}
            ({t(REGIONAL_LEVEL_DATA[regionalLevel].nameI18n)}
            {REGIONAL_LEVEL_DATA[regionalLevel].address_field ? " " : ""}
            {addressData.address[REGIONAL_LEVEL_DATA[regionalLevel].address_field]})
        </span>
    ) : (
        <span> ({t("market.timelines.comparison_regional_levels")})</span>
    );

    const {headers, columns, rows} = timelineTableData;

    const headerGroup = (
        <ColumnGroup>
            <Row>
                <Column header={t("market.timelines.year")} rowSpan={2} />
                {headers.map((h) => (
                    <Column key={h.categoryHeader} header={h.categoryHeader} colSpan={h.columnHeaders.length} />
                ))}
            </Row>
            <Row>
                {headers
                    .flatMap((h) => h.columnHeaders)
                    .map((h) => (
                        <Column key={h} header={h} />
                    ))}
            </Row>
        </ColumnGroup>
    );

    /** @type {Column[]} */
    const columnElements = columns.map((c) => <Column key={c} field={c} />);

    const mapColumnHeader = (header) => {
        if (header === t("market.timelines.span")) return t("market.timelines.from_to");
        if (header === t("market.timelines.price")) return t("market.timelines.avg");
        return header;
    };
    const areaUnit = objectType === SEGMENT.BUERO_K ? "NF" : objectType === SEGMENT.MFHWGH_K ? "WNF" : "WF";
    const mapCategoryHeader = (header) => {
        if (header.startsWith(t("market.timelines.sales_prices")))
            return `Angebots- und Kaufpreise in €/m² ${areaUnit}`;
        if (header.startsWith(t("market.timelines.rent_prices"))) return `Angebotsmiete in €/m² ${areaUnit}`;
        return header;
    };

    const fontStyle = "font-family:Arial;font-size:10pt;";
    const paragraphStyle = `${fontStyle}text-align:justify;text-justify:inter-ideograph;margin:0 0 10pt;`;
    const escapeHtml = (text) =>
        text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");

    //output is meant for copying into word that works with obsolete html attributes better than with style
    const buildHtml = (copyHeaders, copyColumns, headline, bottomline) => {
        const tdStyle = `${fontStyle}border:1px solid black;padding:2pt;`;
        const thStyle = `background-color:#F2F2F2;${tdStyle}`;
        const cellPBoldStyle = `${fontStyle}font-weight:bold;text-align:center;margin-bottom:1pt;`;
        const th = (text, attrs = "") =>
            `<th ${attrs} style="${thStyle}"><p align="center" style="${cellPBoldStyle}">${text}</p></th>`;
        const td = (text) =>
            `<td  style="${tdStyle}"><p align="center" style="${fontStyle};text-align:center;">${text}</p></td>`;

        const yearTh = `<th rowspan="2" width="13%" style="${thStyle}"><p align="center" style="${cellPBoldStyle}">${t("market.timelines.year")}</p></th>`;
        const categoryHeaderCells = copyHeaders
            .map((h) => th(mapCategoryHeader(h.categoryHeader), `colspan="${h.columnHeaders.length}"`))
            .join("");
        const allColumnHeaders = copyHeaders.flatMap((h) => h.columnHeaders);
        const dataColWidth = `${Math.floor(87 / allColumnHeaders.length)}%`;
        const columnHeaderCells = allColumnHeaders
            .map((h) => th(mapColumnHeader(h), `width="${dataColWidth}"`))
            .join("");
        const dataRows = rows.map((row) => `<tr>${copyColumns.map((c) => td(row[c] ?? "")).join("")}</tr>`).join("");

        // Einleitungstext als Fließtext statt Ein-Zellen-Tabelle (#19417), damit Word ihn frei formatieren lässt.
        return `<p align="justify" style="${paragraphStyle}">${headline}</p>
            <table cellspacing="0" style="width:17cm;border-collapse:collapse;">
              <thead>
                <tr>${yearTh}${categoryHeaderCells}</tr>
                <tr>${columnHeaderCells}</tr>
              </thead>
              <tbody>${dataRows}</tbody>
              <tfoot><tr><td colspan="${copyColumns.length}"><p style="${fontStyle}">${bottomline}</p></td></tr></tfoot>
            </table>`;
    };

    const copyTable = async () => {
        const addr = addressData.address;
        const locationPhrase = regionalLevel
            ? ({
                  NEIGHBORHOOD: "in umliegender Nachbarschaft",
                  POSTCODE: `in ${addr.postcode} ${addr.municipality}`,
                  MUNICIPALITY: `in ${addr.municipality}`,
                  DISTRICT: `im Kreis ${addr.district}`,
                  REGION: `in der Region ${addr.region}`,
              }[regionalLevel] ??
              `in ${t(REGIONAL_LEVEL_DATA[regionalLevel].nameI18n)} ${addr[REGIONAL_LEVEL_DATA[regionalLevel].address_field]}`)
            : `im Vergleich verschiedener Raumebenen rund um ${addr.postcode} ${addr.municipality}`;
        const headline = `Nachfolgende Angebots- und Transaktionsdaten wurden für ${t(getObjectTypeLabelI18n(objectType))} ${locationPhrase} recherchiert:`;
        const bottomline = `Werte: bereinigter gleitender Durchschnitt, Quelle: VALUE Marktdaten (www.value-marktdaten.de)`;

        const copyHeaders = [...headers].reverse();
        let colOffset = 1;
        const copyColumns = [
            columns[0],
            ...headers
                .map((h) => {
                    const group = columns.slice(colOffset, colOffset + h.columnHeaders.length);
                    colOffset += h.columnHeaders.length;
                    return group;
                })
                .reverse()
                .flat(),
        ];

        // Immobilienmarkttext als Fließtext statt Ein-Zellen-Tabelle (#19417); Leerzeilen trennen echte Absätze.
        const descriptionHtml = marketDescription
            .split(/\n\s*\n/)
            .map((paragraph) => paragraph.trim())
            .filter(Boolean)
            .map((paragraph) => `<p align="justify" style="${paragraphStyle}">${escapeHtml(paragraph)}</p>`)
            .join("");
        const formatedOutput = descriptionHtml + buildHtml(copyHeaders, copyColumns, headline, bottomline);

        const plainCategoryRow =
            "\t" +
            copyHeaders
                .map((h) => mapCategoryHeader(h.categoryHeader) + "\t".repeat(h.columnHeaders.length - 1))
                .join("\t");
        const plainColumnRow =
            t("market.timelines.year") +
            "\t" +
            copyHeaders.flatMap((h) => h.columnHeaders.map(mapColumnHeader)).join("\t");
        const plainDataRows = rows.map((row) => copyColumns.map((c) => row[c] ?? "").join("\t")).join("\n");
        const plainOutput =
            (marketDescription ? `${marketDescription}\n\n` : "") +
            headline +
            "\n\n" +
            plainCategoryRow +
            "\n" +
            plainColumnRow +
            "\n" +
            plainDataRows +
            "\n\n" +
            bottomline;

        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    [MimeType.TEXT_HTML]: new Blob([formatedOutput], {type: MimeType.TEXT_HTML}),
                    [MimeType.TEXT_PLAIN]: new Blob([plainOutput], {type: MimeType.TEXT_PLAIN}),
                }),
            ]);
            toastRef.current.show({severity: "info", summary: "Info", detail: t("general.dataCopied")});
        } catch (e) {
            console.warn("Could not copy table content to clipboard", e);
        }
    };

    return (
        <div>
            <div className="flex flex-row items-center table-title mb-2 pb-0">
                <h3 className="element-title mt-3 ml-2 mb-0 mr-4">{t("market.timelines.price_development")}</h3>
                <div className="text-surface-500 dark:text-surface-300 flex-1 mt-4">
                    {t(getObjectTypeLabelI18n(objectType))}
                    {regionalInfo}
                </div>
                <i
                    className="pi pi-copy tooltip-icon text-primary cursor-pointer mt-4"
                    data-pr-position="top"
                    data-pr-tooltip={t("general.copyTooltip")}
                    onClick={copyTable}
                />
                <Tooltip target={".tooltip-icon"} className="custom-tooltip" />
            </div>
            <div className="element-panel mb-0">
                <div>
                    <DataTable value={rows} headerColumnGroup={headerGroup}>
                        {columnElements}
                    </DataTable>
                </div>
                <Toast ref={toastRef} position="top-center" />
            </div>
        </div>
    );
}

/**
 * @param {AggregatedTimelineData} timelineData
 * @param {TimelineObjectType} objectType
 * @param {TimelineSegmentType} segmentType
 * @param {boolean} indexed
 * @param {TimelineRegionalLevel|null} regionalLevel
 * @return {JSX.Element|null}
 */
function TimelinesChart({timelineData, objectType, segmentType, indexed, regionalLevel, headerAction}) {
    /** @type {PersistenceAddressData} */
    const addressData = useSelector(selectAddressData);

    const {t, language} = useRevaTranslation();

    /** @type {boolean} */
    const isDarkTheme = useSelector(selectDarkTheme);

    const chartStyle = getChartStyle(isDarkTheme);
    const segment = getTimelineSegments(objectType)[segmentType];

    let header =
        segmentType === TimelineSegmentType.SALE
            ? t("market.timelines.sales_prices")
            : t("market.timelines.rent_prices");
    if (indexed) {
        header += ` (${t("market.timelines.indexed")})`;
    }

    const chartData = getTimelineChartData(
        timelineData,
        segment,
        indexed,
        regionalLevel,
        addressData.address,
        chartStyle,
        t,
    );

    if (!chartData) return null;

    const chartOptions = getTimelineChartOptions(segmentType, indexed, regionalLevel, chartStyle, t, language);

    const regionalInfo = regionalLevel ? (
        <span>
            {" "}
            ({t(REGIONAL_LEVEL_DATA[regionalLevel].nameI18n)}
            {REGIONAL_LEVEL_DATA[regionalLevel].address_field ? " " : ""}
            {addressData.address[REGIONAL_LEVEL_DATA[regionalLevel].address_field]})
        </span>
    ) : null;

    return (
        <div>
            <div className="flex flex-row items-center table-title mb-2">
                <h3 className="element-title mt-3 ml-2 mb-0">{header}</h3>
                <span className="text-surface-500 dark:text-surface-300 ml-4 mt-4">
                    {t(getObjectTypeLabelI18n(objectType))}
                    {regionalInfo}
                </span>
                {headerAction && <div className="ml-auto">{headerAction}</div>}
            </div>
            <div className="element-panel mb-4">
                <div className="w-full h-[25rem] bg-surface-0 dark:bg-surface-900">
                    <Chart type="line" className="w-full h-full" data={chartData} options={chartOptions} />
                </div>
            </div>
        </div>
    );
}

/**
 * @param {AggregatedTimelineData} timelineData
 * @return {React.ReactNode}
 */
function TimelinesData({timelineData}) {
    const {t, language} = useRevaTranslation();
    const dispatch = useDispatch();
    const addressSelected = useSelector(selectAddressSelected);
    const segment = useSelector(selectSegment);
    const {parameterValues: buildingProjectValue} = useParameterValue("building_project", "AVM");
    const [docDownloading, setDocDownloading] = useState(false);
    const docToastRef = useRef(null);

    const downloadDocumentation = async () => {
        setDocDownloading(true);
        try {
            const {blob, filename} = await dispatch(fetchTimelinesDocumentation());
            openBlobForDownload(blob, filename ?? "REVA-Zeitreihen-Dokumentation.pdf");
        } catch (e) {
            console.error("Failed to download timelines documentation", e);
            docToastRef.current.show({
                severity: "error",
                summary: "Fehler",
                detail: t("market.timelines.documentation_download_error"),
                life: 5000,
            });
        } finally {
            setDocDownloading(false);
        }
    };

    const supportedObjectTypes = getObjectTypes(timelineData);

    const [objectType, setObjectType] = useState(
        getObjectTypeBySegment(segment, buildingProjectValue?.activeParameterValue),
    );

    const [indexed, setIndexed] = useState(false);

    const objectTypeOptions = supportedObjectTypes.map((value) => {
        return {
            label: t(getObjectTypeLabelI18n(value)),
            value,
        };
    });

    const existingRegionalLevels = getSupportedRegionalLevelsForObjectType(timelineData, objectType);

    const [regionalLevel, setRegionalLevel] = useState(existingRegionalLevels.length ? existingRegionalLevels[0] : "");
    const [regionalLevelsAll, setRegionalLevelsAll] = useState(false);
    const effectiveRegionalLevel = regionalLevelsAll ? null : regionalLevel;

    const changeObjectType = (e) => {
        const newObjectType = e.value;
        setObjectType(newObjectType);
        const existingRegionalLevelsForNew = getSupportedRegionalLevelsForObjectType(timelineData, newObjectType);
        setRegionalLevel(existingRegionalLevelsForNew.length ? existingRegionalLevelsForNew[0] : "");
    };

    /**
     * @param {SelectButtonChangeEvent} e
     */
    const updateRegionalLevel = (e) => {
        if (e.value && existingRegionalLevels.includes(e.value)) {
            setRegionalLevel(e.value);
        }
    };

    const timelineTableData = getTimelineTableData(
        timelineData,
        objectType,
        indexed,
        effectiveRegionalLevel,
        t,
        language,
    );

    if (!timelineData || !addressSelected) return null;

    const regionLevelSelections = TIMELINE_REGIONAL_LEVEL_VALUES.map((level) => {
        const levelData = REGIONAL_LEVEL_DATA[level];
        return {
            label: t(levelData.nameI18n),
            value: level,
            disabled: !existingRegionalLevels.includes(level),
        };
    });

    return (
        <div className="grid grid-cols-12 gap-4 m-0">
            <div className="col-span-12 lg:col-span-5 p-0 pr-2">
                <div className="element-panel m-0 px-2 pt-2 mb-4 pb-2">
                    <label htmlFor="timelines-object-type" className="input-label font-bold !mt-2 block">
                        {t("market.timelines.object_type")}
                    </label>
                    <Dropdown
                        inputId="timelines-object-type"
                        value={objectType}
                        options={objectTypeOptions}
                        className="md:w-[26rem] mb-4"
                        onChange={changeObjectType}
                    />
                    <label id="timelines-regional-level-label" className="input-label font-bold !mt-2 block">
                        {t("market.timelines.regional_level")}
                    </label>
                    <SelectButton
                        id="timelines-regional-level"
                        aria-labelledby="timelines-regional-level-label"
                        value={effectiveRegionalLevel}
                        onChange={updateRegionalLevel}
                        options={regionLevelSelections}
                        optionDisabled="disabled"
                        disabled={regionalLevelsAll}
                        className="md:w-auto mb-4"
                        pt={{
                            button: ({context}) => ({
                                className: context.selected
                                    ? "border text-primary bg-surface-0 dark:bg-surface-900"
                                    : "",
                            }),
                        }}
                    />
                    <div className="flex items-center gap-6 pb-2">
                        <div>
                            <Checkbox
                                inputId="compareRegionalLevels"
                                name="compareRegionalLevels"
                                value={regionalLevelsAll}
                                onChange={(e) => setRegionalLevelsAll(e.checked)}
                                checked={regionalLevelsAll}
                            />
                            <label htmlFor="compareRegionalLevels" className="ml-4">
                                {t("market.timelines.comparison_regional_levels")}
                            </label>
                        </div>
                        <div>
                            <Checkbox
                                inputId="indexed"
                                name="indexed"
                                value={indexed}
                                onChange={(e) => setIndexed(e.checked)}
                                checked={indexed}
                            />
                            <label htmlFor="indexed" className="ml-4">
                                {t("market.timelines.indexed")}
                            </label>
                        </div>
                    </div>
                </div>
                <TimelinesTable
                    objectType={objectType}
                    regionalLevel={effectiveRegionalLevel}
                    timelineTableData={timelineTableData}
                />
            </div>
            <div className="col-span-12 lg:col-span-7 py-0 lg:mt-0">
                <TimelinesChart
                    timelineData={timelineData}
                    objectType={objectType}
                    segmentType={TimelineSegmentType.SALE}
                    indexed={indexed}
                    regionalLevel={effectiveRegionalLevel}
                    headerAction={
                        <>
                            <Button
                                label={t("market.timelines.documentation_download")}
                                icon="pi pi-file-pdf"
                                className="p-button-text p-button-sm"
                                loading={docDownloading}
                                onClick={downloadDocumentation}
                            />
                            <Toast ref={docToastRef} position="top-center" />
                        </>
                    }
                />
                <TimelinesChart
                    timelineData={timelineData}
                    objectType={objectType}
                    segmentType={TimelineSegmentType.RENT}
                    indexed={indexed}
                    regionalLevel={effectiveRegionalLevel}
                />
            </div>
        </div>
    );
}

/**
 * @returns {React.ReactNode}
 */
export function TabTimeLines() {
    const persistenceLoading = useSelector(selectPersistenceLoading);
    const timelineLoading = useRevaSelector(selectTimelinesLoading);
    const timeLineError = useRevaSelector(selectTimelinesError);
    const timelineData = useRevaSelector(selectTimelineDataAggregated);

    const permitted = useCheckPermission(PERMISSION_RULE_MARKET_TIMELINES);

    const loading = persistenceLoading || timelineLoading;

    // #19552: Der Reiter Markt ist auch ohne Zeitreihen-Recht offen; ohne Recht kommt vom AVM nur ein 403.
    if (!permitted) {
        return <MissingPermissionIndicator missingPermissions={true} />;
    }

    if (timeLineError) {
        return <ErrorIndicator errorObject={timeLineError} titleI18nKey="market.timelines.error" />;
    }

    if (loading || !timelineData) return <LoadingIndicator loading={true} />;

    return (
        <div className="flex-1">
            <MissingPermissionIndicator permissionRule={PERMISSION_RULE_MARKET_TIMELINES}>
                <PersistenceFinalMessage />
                <SnapshotMessage />
                <AppreciationTabInputText page="market" tab="timelines" />
                <TimelinesData timelineData={timelineData} />
            </MissingPermissionIndicator>
        </div>
    );
}

export default TabTimeLines;
